/* eslint-disable react/jsx-closing-tag-location */
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Box, Button, CircularProgress, FormHelperText, IconButton, Link, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import Download from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { bytesToSize } from '../../utils/bytesToSize';
import { useTheme } from '@mui/material/styles';
import { formatInZonedTime } from '../../utils/dateTime';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';
import { useEffect, useState } from 'react';

export const FileDropzone = (props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    accept,
    disabled,
    files = [],
    getFilesFromEvent,
    maxFiles,
    maxSize,
    minSize,
    noClick,
    noDrag,
    noDragEventsBubbling,
    noKeyboard,
    onDrop,
    onDropAccepted,
    onDropRejected,
    onFileDialogCancel,
    onRemove,
    onRemoveAll,
    onUpload,
    preventDropOnDocument,
    hideRemoveAll,
    hideUpload,
    error,
    helperText,
    hideFileList,
    isHideSelectFileText = false,
    canDownload,
    onClickDownload,
    ...other
  } = props;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    onDrop,
    disabled,
    onDropRejected,
  });

  const isError = !!error;
  const dropzoneContent = (
    <>
      <Box
        sx={{
          alignItems: 'center',
          border: 1,
          borderRadius: 1,
          borderStyle: 'dashed',
          borderColor: !isError ? 'divider' : theme.palette.error.main,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          outline: 'none',
          p: 2,
          ...(isDragActive && {
            backgroundColor: 'action.active',
            opacity: 0.5,
          }),
          '&:hover': {
            backgroundColor: 'action.hover',
            cursor: 'pointer',
            opacity: 0.5,
          },
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <Box
          sx={{
            '& img': {
              width: 100,
            },
          }}
        >
          <img alt="Select file" src="/static/images/undraw_add_file2_gvbb.svg" />
        </Box>

        {!isHideSelectFileText && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">{(maxFiles && maxFiles === 1) ? t('Label_Select_File') : t('Label_Select_Files')}</Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                {(maxFiles && maxFiles === 1) ? t('Label_Drop_File') : t('Label_Drop_Files')}
                &nbsp;<Link underline="always">{ t('browse') }</Link> { t('thorough_your_machine') }
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      {isError && <FormHelperText error>{helperText}</FormHelperText>}
    </>
  );

  return (
    <div {...other}>
      {dropzoneContent}

      {!hideFileList && files.length > 0 && (
        <Box
          sx={{ mt: 2 }} style={{
            maxHeight: '18em',
            overflowY: 'auto',
            padding: '0 0.3em'
          }}
        >
          <List>
            {files.map((file) => (
              <CustomListItme
                key={`${file?.path}${file?.DocumentID}`}
                capsDetails={file} props={props}
              />
            ))}
          </List>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2,
            }}
          >
            {!hideRemoveAll && (
              <Button onClick={onRemoveAll} size="small" type="button">
                Remove All
              </Button>
            )}

            {!hideUpload && (
              <Button onClick={onUpload} size="small" sx={{ ml: 2 }} type="button" variant="contained">
                Upload
              </Button>
            )}
          </Box>
        </Box>
      )}
    </div>
  );
};

FileDropzone.propTypes = {
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  disabled: PropTypes.bool,
  files: PropTypes.array,
  getFilesFromEvent: PropTypes.func,
  maxFiles: PropTypes.number,
  maxSize: PropTypes.number,
  minSize: PropTypes.number,
  noClick: PropTypes.bool,
  noDrag: PropTypes.bool,
  noDragEventsBubbling: PropTypes.bool,
  noKeyboard: PropTypes.bool,
  onDrop: PropTypes.func,
  onDropAccepted: PropTypes.func,
  onDropRejected: PropTypes.func,
  onFileDialogCancel: PropTypes.func,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  onUpload: PropTypes.func,
  preventDropOnDocument: PropTypes.bool,
  canDownload: PropTypes.bool,
  onClickDownload: PropTypes.func
};

function CustomListItme({ capsDetails, props }) {
  const { dateFormat, timezone } = useAuth();
  const { onRemove, onClickDownload, files } = props;
  const [isDeleteProgress, setIsDeleteProgress] = useState(false);
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);

  useEffect(() => {
    setIsDeleteProgress(false);
  }, [files]);

  const CapView = (args) => {
    const { file } = args;
    const fileName = <>{ file.name}<br /></>;
    const createdBy = file?.CreatedBy && <>{file?.CreatedBy}<br /></>;
    const createdOn = file?.CreatedOn && <>{formatInZonedTime(file?.CreatedOn, dateFormat, timezone)}<br /></>;
    return (
      <div style={{ whiteSpace: 'nowrap' }}>
        { fileName }
        { createdBy }
        { createdOn }
      </div>
    );
  };

  const CapTooltipView = ({ file }) => {
    const fileName = file.name && <>{file.name}<br /></>;
    const createdBy = file?.CreatedBy && <>{file?.CreatedBy}<br /></>;
    const createdOn = file?.CreatedOn && <>{formatInZonedTime(file?.CreatedOn, dateFormat, timezone)}<br /></>;
    return (
      <div>
        { fileName }
        { createdBy }
        { createdOn }
      </div>
    );
  };

  const onClickDeleteCapsule = (details) => {
    if (onRemove) {
      onRemove(details);
      setIsDeleteProgress(true);
    }
  };

  const onClickDownloadFile = (details) => {
    onClickDownload(details);
    setIsDownloadingFile(true);
    setTimeout(() => {
      setIsDownloadingFile(false);
    }, 6565);
  };

  const CanDownloadView = ({ lodng }) => (lodng ? <CircularProgress size={14} />
    : <Tooltip title="download" arrow>
      <IconButton edge="end" onClick={() => onClickDownloadFile(capsDetails)}>
        <Download fontSize="small" />
      </IconButton>
    </Tooltip>);

  return (<ListItem
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      '& + &': {
        mt: 1,
      },
    }}
  >
    <ListItemIcon>
      {
      props?.canDownload ? <CanDownloadView lodng={isDownloadingFile} /> : <FileCopyIcon fontSize="small" />
      }
    </ListItemIcon>
    <Tooltip title={<CapTooltipView file={capsDetails} />} placement="top" arrow>
      <ListItemText
        primary={<CapView file={capsDetails} />}
        primaryTypographyProps={{
          color: 'textPrimary',
          variant: 'subtitle2',
          style: { overflow: 'hidden', textOverflow: 'ellipsis' },
        }}
        secondary={capsDetails.size && bytesToSize(capsDetails.size)}
      />
    </Tooltip>

    <Tooltip title="Remove" arrow>
      <IconButton edge="end" onClick={() => onClickDeleteCapsule(capsDetails)}>
        { isDeleteProgress ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" /> }
      </IconButton>
    </Tooltip>
  </ListItem>);
}
